/* eslint-disable max-len */
import {
  ClockCircleOutlined,
  CloudOutlined,
  DeleteOutlined,
  HistoryOutlined,
  PlaySquareOutlined,
  PlusOutlined,
  SaveOutlined,
  SmileOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Collapse,
  Divider,
  Empty,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from 'antd';
import Preview from 'content/library/components/main/Preview';
import { VideoAssetSet } from 'content/library/types/contentVideoType';
import { getStatusDisplay } from 'content/library/utils';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { videoService } from 'service/api/videoService';
import { LANGUAGE_OPTIONS, VideoSubtitleType } from 'type/videoSubtitleType';
import { VideoType } from 'type/videoType';
import { formatDuration } from '../../../../../shared/utils/timeFormat';
import { VideoAssetsService } from './services/videoAssetsService';
import {
  ContentVideoAssetHistory,
  SERVICE_OPTIONS,
  StreamManifest,
  VideoSearchParams,
  VideoSearchResult,
} from './types/videoAssetsType';
import './VideoAssets.css';

const { Title, Text } = Typography;
const { Option } = Select;

const VideoAssets: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [savingStates, setSavingStates] = useState<{ [key: number]: boolean }>({});
  const [searchedVideo, setSearchedVideo] = useState<VideoType[]>([]);
  const [videoSearchResult, setVideoSearchResult] = useState<VideoSearchResult>({
    data: [],
    count: 0,
  });
  const [contentVideoAssetHistory, setContentVideoAssetHistory] = useState<
    ContentVideoAssetHistory[]
  >([]);
  const [videoAssetSets, setVideoAssetSets] = useState<VideoAssetSet[]>([]);
  const [originalSets, setOriginalSets] = useState<VideoAssetSet[]>([]);
  const [videoSubtitles, setVideoSubtitles] = useState<{
    [id: number]: VideoSubtitleType[];
  }>({});
  const [contentVideoAssetSubtitleMap, setContentVideoAssetSubtitleMap] = useState<
    Record<number, number[]>
  >({});

  const [videoSelectModalVisible, setVideoSelectModalVisible] = useState(false);
  const [currentEditingIndex, setCurrentEditingIndex] = useState<number | null>(null);
  const [videoSearchQuery, setVideoSearchQuery] = useState('');
  const [videoSourceFilter, setVideoSourceFilter] = useState<string>('');
  const [videoStatusFilter, setVideoStatusFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const searchVideo = async (params?: VideoSearchParams) => {
    try {
      const response = await VideoAssetsService.searchVideos(params || {});
      setVideoSearchResult(response);
      // Store data separately for existing compatibility
      setSearchedVideo(response.data);
    } catch (error: any) {
      // Redirect to page 1 for 404 error or page-related errors
      if (error?.response?.status === 404 && params?.page && params.page > 1) {
        message.warning('Could not find requested page. Moving to first page.');
        setCurrentPage(1);
        // Request only page 1 to prevent infinite recursion
        const firstPageParams = {
          ...params,
          page: 1,
        };

        try {
          const response = await VideoAssetsService.searchVideos(firstPageParams);
          setVideoSearchResult(response);
          setSearchedVideo(response.data);
        } catch (retryError) {
          console.error('Error on retry with page 1:', retryError);
          Modal.error({ title: 'Error', content: 'An error occurred while searching videos.', okText: 'Confirm' });
          // Set empty results
          setVideoSearchResult({ data: [], count: 0 });
          setSearchedVideo([]);
        }
      } else {
        console.error('Error searching videos:', error);
        Modal.error({ title: 'Error', content: 'An error occurred while searching videos.', okText: 'Confirm' });
        // Set empty results
        setVideoSearchResult({ data: [], count: 0 });
        setSearchedVideo([]);
      }
    }
  };

  // Open video selection modal
  const openVideoSelectModal = (assetIndex: number) => {
    setCurrentEditingIndex(assetIndex);
    setVideoSelectModalVisible(true);
    setCurrentPage(1); // Reset to first page when modal opens
    // Load video list when modal opens
    searchVideo({
      search: videoSearchQuery,
      source: videoSourceFilter,
      status: videoStatusFilter,
      page: 1,
      pageSize: pageSize,
    });
  };

  // Close video selection modal
  const closeVideoSelectModal = () => {
    setVideoSelectModalVisible(false);
    setCurrentEditingIndex(null);
    setVideoSearchQuery('');
    setVideoSourceFilter('');
    setVideoStatusFilter('');
    setCurrentPage(1);
    setVideoSearchResult({ data: [], count: 0 });
  };

  // Handle video selection
  const handleVideoSelect = (video: VideoType) => {
    if (currentEditingIndex !== null) {
      updateAssetSet(currentEditingIndex, 'video', video.id);
      closeVideoSelectModal();
    }
  };

  // Execute search in video modal
  const handleVideoSearch = () => {
    setCurrentPage(1); // Reset to first page when searching
    searchVideo({
      search: videoSearchQuery,
      source: videoSourceFilter,
      status: videoStatusFilter,
      page: 1,
      pageSize: pageSize,
    });
  };

  // Pagination change handler
  const handlePageChange = (page: number, size?: number) => {
    const newPageSize = size || pageSize;
    setCurrentPage(page);
    if (size !== pageSize) {
      setPageSize(newPageSize);
    }

    searchVideo({
      search: videoSearchQuery,
      source: videoSourceFilter,
      status: videoStatusFilter,
      page: page,
      pageSize: newPageSize,
    });
  };

  const fetchData = async (): Promise<void> => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await VideoAssetsService.searchVideos({});
      setSearchedVideo(response.data);

      // 1. Get list of asset sets mapped to current content
      const contentVideoAssetSets: VideoAssetSet[] =
        await VideoAssetsService.fetchContentVideoAssets(Number(id));

      // Fetch latest subtitles for each video to ensure we have the most up-to-date data
      const videoIds = contentVideoAssetSets
        .filter(assetSet => assetSet.video)
        .map(assetSet => assetSet.video!);

      const contentVideoAssetSubtitleMap = contentVideoAssetSets.reduce(
        (acc, assetSet) => {
          if (assetSet.subtitles) {
            const assetId = assetSet.id!;
            acc[assetId] = assetSet.subtitles.map(subtitle => subtitle.id!);
          }
          return acc;
        },
        {} as Record<number, number[]>
      );
      setContentVideoAssetSubtitleMap(contentVideoAssetSubtitleMap);
      // Fetch subtitles for all videos in parallel to get latest data
      const videoSubtitlesMap = await VideoAssetsService.fetchMultipleVideoSubtitles(videoIds);

      setVideoAssetSets(contentVideoAssetSets);
      setVideoSubtitles(videoSubtitlesMap);

      // Get content video asset history
      const contentVideoAssetHistory = await VideoAssetsService.fetchContentVideoAssetHistory(
        Number(id)
      );
      setContentVideoAssetHistory(contentVideoAssetHistory);

      // VideoSubtitles state is now set above with fetched data

      setOriginalSets(JSON.parse(JSON.stringify(contentVideoAssetSets))); // Deep copy
    } catch (error) {
      console.error('Error fetching data:', error);
      Modal.error({ title: 'Error', content: 'Failed to load data.', okText: 'Confirm' });
    } finally {
      setLoading(false);
    }
  };

  // Add new Video Asset Set with temporary key
  const addNewAssetSet = () => {
    const hasDefaultAsset = videoAssetSets.some(asset => asset.is_default);
    const shouldBeDefault = !hasDefaultAsset;

    const newSet: VideoAssetSet = {
      content: Number(id),
      video: null,
      subtitle_ids: null,
      service_name: '', // Default service name if default asset
      isNew: true,
      is_default: shouldBeDefault, // Set as default if no default asset exists
      is_subtitles_embedded: false,
      stream_manifests: null,
      tempId: Date.now(), // Add temporary unique identifier
      after_add_subtitle: true,
    };
    setVideoAssetSets([...videoAssetSets, newSet]);
  };

  // Delete Video Asset Set - Immediate deletion
  const removeAssetSet = async (targetIndex: number) => {
    const targetSet = videoAssetSets[targetIndex];

    try {
      // If it's an existing asset (has actual ID), delete from server
      if (targetSet.id && !targetSet.isNew) {
        await VideoAssetsService.deleteContentVideoAsset(targetSet.id);
        message.success('Video asset deleted successfully.');
      }

      // Remove from local state
      const newSets = videoAssetSets.filter((set, index) => {
        // Compare by ID if exists, otherwise by tempId
        if (targetSet.id && set.id) {
          return set.id !== targetSet.id;
        } else if (targetSet.tempId && set.tempId) {
          return set.tempId !== targetSet.tempId;
        }
        return index !== targetIndex;
      });
      setVideoAssetSets(newSets);

      // Refresh data to get updated list
      await fetchData();
    } catch (error) {
      console.error('Error deleting video asset:', error);
      Modal.error({ title: 'Error', content: 'Failed to delete video asset.', okText: 'Confirm' });
    }
  };

  // Update Video Asset Set
  const updateAssetSet = async (index: number, field: keyof VideoAssetSet, value: any) => {
    const newSets = [...videoAssetSets];
    newSets[index] = { ...newSets[index], [field]: value };

    if (field === 'subtitle_ids') {
      contentVideoAssetSubtitleMap[newSets[index].id!] = value;
    }

    if (field === 'video' && value) {
      try {
        const subtitles = await VideoAssetsService.fetchVideoSubtitles(value);

        // Update the assetSet's subtitles directly with latest data
        newSets[index].subtitles = subtitles;

        // Reset subtitle selection (user needs to manually select)
        contentVideoAssetSubtitleMap[newSets[index].id!] = [];

        // Find and update stream_manifests from selected video
        const selectedVideo = searchedVideo.find(v => v.id === value);
        if (selectedVideo && selectedVideo.stream_manifests) {
          // Convert video's stream_manifests to assetSet's stream_manifests format
          newSets[index].stream_manifests = selectedVideo.stream_manifests.map(manifest => ({
            type: manifest.type,
            preset: manifest.preset,
            stream_url: manifest.stream_url_full || manifest.stream_url,
            status: manifest.status,
            created: manifest.created,
          }));
        } else {
          newSets[index].stream_manifests = [];
        }
      } catch (error) {
        console.error('Error fetching subtitles:', error);
        Modal.error({ title: 'Error', content: 'Failed to load subtitle information.', okText: 'Confirm' });
        newSets[index].subtitles = [];
        contentVideoAssetSubtitleMap[newSets[index].id!] = [];
        newSets[index].stream_manifests = [];
      }
    }

    // Reset subtitle_ids and stream_manifests if video is cleared
    if (field === 'video' && !value) {
      contentVideoAssetSubtitleMap[newSets[index].id!] = [];
      newSets[index].subtitles = [];
      newSets[index].stream_manifests = [];
    }

    // Update video name when video changes
    if (field === 'video') {
      newSets[index].video_name = getVideoName(value);
    }

    setVideoAssetSets(newSets);
  };

  // Individual save for each asset set
  const handleIndividualSave = async (index: number) => {
    if (!id) return;

    const assetSet = videoAssetSets[index];
    const assetKey = assetSet.id || assetSet.tempId || index;

    setSavingStates(prev => ({ ...prev, [assetKey]: true }));

    try {
      if (assetSet.isNew) {
        // Save new asset set
        await VideoAssetsService.saveContentVideoAssets(Number(id), [assetSet]);
        message.success('Video asset saved successfully.');
      } else {
        // Update existing asset set
        await VideoAssetsService.updateContentVideoAsset(assetSet.id!, {
          video: assetSet.video!,
          subtitle_ids: assetSet.subtitle_ids,
          is_default: assetSet.is_default,
          service_name: assetSet.service_name,
          stream_manifests: assetSet.stream_manifests,
          is_subtitles_embedded: assetSet.is_subtitles_embedded,
        });
        message.success('Video asset updated successfully.');
      }

      // Refresh data to get updated state
      await fetchData();
    } catch (error) {
      console.error('Error saving video asset:', error);
      Modal.error({ title: 'Error', content: 'Failed to save video asset.', okText: 'Confirm' });
    } finally {
      setSavingStates(prev => ({ ...prev, [assetKey]: false }));
    }
  };

  // Video transcoding

  // Check if individual asset has changes
  const hasAssetChanges = (assetSet: VideoAssetSet, _index: number) => {
    if (assetSet.isNew) return true;

    const original = originalSets.find(orig => orig.id === assetSet.id);
    if (!original) return false;

    return (
      original.video !== assetSet.video ||
      JSON.stringify(original.subtitle_ids) !== JSON.stringify(assetSet.subtitle_ids) ||
      original.service_name !== assetSet.service_name ||
      original.is_subtitles_embedded !== assetSet.is_subtitles_embedded
    );
  };
  // Render video options
  const getVideoName = (videoId: number | null) => {
    if (!videoId) return '';
    const video = searchedVideo.find(v => v.id === videoId);
    return `Video ${videoId}` || video?.file_name || '';
  };

  // Render subtitle options
  const getSubtitleName = (videoId: number | null, subtitleIds: number[] | null) => {
    if (!videoId || !subtitleIds || subtitleIds.length === 0) return '';
    const subtitleNames = subtitleIds.map(subtitleId => {
      const subtitle = videoSubtitles[videoId!]?.find(s => s.id === subtitleId);
      return subtitle?.lang || `Subtitle ${subtitleId}`;
    });
    return subtitleNames.join(', ');
  };

  const handleVideoTranscoding = async (
    contentId: number,
    videoId: number,
    subtitleIds: number[],
    serviceName: string,
    assetId: number
  ) => {
    await videoService.videoTranscode(contentId, videoId, subtitleIds, serviceName, assetId);
  };

  // Render Stream Manifests options
  const getStreamManifests = (streamManifests: StreamManifest[] | null) => {
    // Handle null or empty array
    if (!streamManifests || streamManifests.length === 0) {
      return <div>Empty Transcode Video</div>;
    }

    // Filter manifests that have stream_url (regardless of status)
    const availableManifests = streamManifests.filter(
      manifest => manifest.stream_url || manifest.stream_url_full
    );
    const inProgressManifests = streamManifests.filter(
      manifest => manifest.status === 'in_progress'
    );

    if (inProgressManifests.length > 0) {
      return <Spin>Some Transcode Video is in Progress</Spin>;
    }

    if (availableManifests.length === 0) {
      return <div>Empty Transcode Video</div>;
    }

    return (
      <Collapse size="small" ghost>
        <Collapse.Panel header={`${availableManifests.length} Converted Files`} key="1">
          {availableManifests.map((manifest, index) => {
            // Use stream_url_full if available, otherwise use stream_url
            const displayUrl = manifest.stream_url_full || manifest.stream_url;

            if (!displayUrl) return null;

            return (
              <div key={index} className="video-assets-stream-manifest">
                <div className="video-assets-stream-manifest-header">
                  <span style={{ fontWeight: 'bold' }}>
                    {manifest.type.toUpperCase()} - {manifest.preset}
                  </span>
                  <Tag
                    color={
                      manifest.status === 'completed'
                        ? 'green'
                        : manifest.status === 'in_progress'
                          ? 'blue'
                          : 'default'
                    }
                    className="video-assets-status-tag"
                  >
                    {manifest.status}
                  </Tag>
                </div>

                {/* Preview for stream manifest */}
                <div className="video-assets-stream-manifest-preview">
                  <Preview videoUrl={displayUrl} />
                </div>
              </div>
            );
          })}
        </Collapse.Panel>
      </Collapse>
    );
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  if (loading) {
    return <Spin size="large" className="video-assets-loading" />;
  }

  return (
    <div className="video-assets-container">
      {/* Header */}
      <div className="video-assets-header">
        <div className="video-assets-header-content">
          <Title level={3} className="video-assets-header-title">
            Video Profile Management
          </Title>
          <Text type="secondary" className="video-assets-header-subtitle">
            Manage videos, subtitles, and services by content
          </Text>
        </div>
        <Space wrap>
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={addNewAssetSet}
            size="large"
            style={{ minWidth: '140px' }}
          >
            Add Video Profile
          </Button>
        </Space>
      </div>

      <Divider />
      {/* Video Asset Set Cards */}
      {videoAssetSets.length === 0 ? (
        <Empty
          description="No registered video asset sets"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          className="video-assets-empty"
        >
          <Button type="primary" icon={<PlusOutlined />} onClick={addNewAssetSet}>
            Add First Asset Set
          </Button>
        </Empty>
      ) : (
        <Row gutter={[16, 16]} style={{ alignItems: 'stretch' }}>
          {videoAssetSets
            .sort((a, b) => {
              // Default assets first
              if (a.is_default && !b.is_default) return -1;
              if (!a.is_default && b.is_default) return 1;
              return 0;
            })
            .map((assetSet, index) => {
              const assetKey = assetSet.id || assetSet.tempId || index;
              const isSaving = savingStates[assetKey] || false;
              const hasChanges = hasAssetChanges(assetSet, index);

              return (
                <Col xs={24} sm={12} lg={8} key={assetKey} style={{ height: '100%' }}>
                  <Card
                    title={
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Text strong>
                          {assetSet.is_default
                            ? `Default video profile`
                            : `Video profile #${index + 1}`}
                        </Text>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {assetSet.video && (
                            <Button
                              type="link"
                              onClick={() =>
                                handleVideoTranscoding(
                                  Number(id),
                                  assetSet.video!,
                                  contentVideoAssetSubtitleMap[assetSet.id!] ||
                                    assetSet.subtitle_ids ||
                                    [],
                                  assetSet.service_name!,
                                  assetSet.id!
                                )
                              }
                            >
                              ReTranscode
                            </Button>
                          )}
                        </div>
                      </div>
                    }
                    extra={
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {/* Individual Save Button */}
                        <Button
                          type="primary"
                          icon={<SaveOutlined />}
                          onClick={() => handleIndividualSave(index)}
                          loading={isSaving}
                          disabled={!hasChanges}
                          size="small"
                        >
                          Save
                        </Button>
                        {/* Delete Button */}
                        {!assetSet.is_default && (
                          <Tooltip title="Delete">
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => removeAssetSet(index)}
                              size="small"
                            />
                          </Tooltip>
                        )}
                      </div>
                    }
                    className={`video-assets-card ${
                      assetSet.isNew ? 'new' : assetSet.is_default ? 'default' : 'normal'
                    }`}
                  >
                    <div className="video-assets-card-content">
                      {/* Top content area */}
                      <Space direction="vertical" size="middle" className="video-assets-card-top">
                        {/* Video selection with preview */}
                        <div>
                          <div className="video-assets-section">
                            <PlaySquareOutlined className="video-assets-section-icon video" />
                            <Text strong>Video</Text>
                          </div>

                          {/* Video preview - show if video is selected */}
                          <Tooltip
                            title={assetSet.video_name || 'Click to select a video'}
                            placement="top"
                          >
                            <Button
                              type="default"
                              onClick={() => openVideoSelectModal(index)}
                              className="video-assets-video-button"
                            >
                              <span className="video-assets-video-button-text">
                                {assetSet.video_name || 'Select Video'}
                              </span>
                            </Button>
                          </Tooltip>
                        </div>
                        {/* Subtitle selection */}
                        <div>
                          <div className="video-assets-section">
                            <SmileOutlined className="video-assets-section-icon subtitle" />
                            <Text strong>Subtitle</Text>
                            <Tooltip title="Page to add Video Subtitle">
                              <Button
                                type="link"
                                onClick={() => navigate(`/assets/edit/${assetSet.video}`)}
                              >
                                Move
                              </Button>
                            </Tooltip>
                          </div>
                          <Select
                            mode="multiple"
                            style={{ width: '100%' }}
                            placeholder="Select subtitles (multiple selection possible)"
                            value={
                              contentVideoAssetSubtitleMap[assetSet.id!] || assetSet.subtitle_ids
                            }
                            onChange={value => updateAssetSet(index, 'subtitle_ids', value)}
                            showSearch={false}
                          >
                            {(() => {
                              return videoSubtitles[assetSet.video!]?.map(subtitle => {
                                const langLabel = LANGUAGE_OPTIONS.find(
                                  lang => lang.value === subtitle.lang
                                )?.label;
                                const langFlag = LANGUAGE_OPTIONS.find(
                                  lang => lang.value === subtitle.lang
                                )?.flag;
                                const subtitleName =
                                  subtitle.file_url?.split('/').pop() || subtitle.id || 'Untitled';

                                return (
                                  <Option key={subtitle.id} value={subtitle.id}>
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <span style={{ fontWeight: 'bold' }}>
                                        {subtitleName} ({langLabel} {langFlag})
                                      </span>
                                    </div>
                                  </Option>
                                );
                              });
                            })()}
                          </Select>
                        </div>

                        {/* Service name - Show only if not default asset set */}
                        {!assetSet.is_default && (
                          <div>
                            <div className="video-assets-section">
                              <CloudOutlined className="video-assets-section-icon service" />
                              <Text strong>Service Name</Text>
                            </div>
                            <Select
                              style={{ width: '100%' }}
                              placeholder="Select or enter service"
                              value={assetSet.service_name}
                              onChange={value => updateAssetSet(index, 'service_name', value)}
                              disabled={assetSet.is_default}
                            >
                              {SERVICE_OPTIONS.map(service => (
                                <Option key={service} value={service}>
                                  {service}
                                </Option>
                              ))}
                            </Select>
                          </div>
                        )}
                      </Space>

                      {/* Bottom summary area */}
                      <div className="video-assets-card-bottom">
                        <div className="video-assets-card-bottom-item">
                          Video: <Text>{getVideoName(assetSet.video)}</Text>
                        </div>
                        <div className="video-assets-card-bottom-item">
                          Subtitle:{' '}
                          <Text>
                            {getSubtitleName(
                              assetSet.video,
                              contentVideoAssetSubtitleMap[assetSet.id!] || assetSet.subtitle_ids
                            )}
                          </Text>
                        </div>
                        {!assetSet.is_default && (
                          <div className="video-assets-card-bottom-item">
                            Service: <Text>{assetSet.service_name}</Text>
                          </div>
                        )}
                        {assetSet.is_default && (
                          <div className="video-assets-card-bottom-item">
                            Type:{' '}
                            <Text style={{ color: '#52c41a', fontWeight: 'bold' }}>
                              Default Asset
                            </Text>
                          </div>
                        )}
                        <div className="video-assets-card-bottom-item">
                          <div style={{ marginBottom: '4px' }}>Manifests:</div>
                          {getStreamManifests(assetSet.stream_manifests || [])}
                        </div>
                      </div>
                    </div>
                  </Card>
                </Col>
              );
            })}
        </Row>
      )}

      {/* Content Video Asset History */}
      <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
        <Col xs={24}>
          <Card
            title={
              <div className="video-assets-history-header">
                <HistoryOutlined className="video-assets-history-icon" />
                <span>Content Video Asset History</span>
                <Tag color="blue" className="video-assets-history-tag">
                  {contentVideoAssetHistory.length} Actions
                </Tag>
              </div>
            }
            className="video-assets-history-card"
          >
            {contentVideoAssetHistory.length > 0 ? (
              <Timeline>
                {contentVideoAssetHistory
                  .sort((a, b) => b.id - a.id)
                  .map((history, _index) => (
                    <Timeline.Item
                      key={history.id}
                      color={
                        history.status === 'COMPLETED' || history.status === 'completed'
                          ? 'green'
                          : history.status === 'FAILED' || history.status === 'failed'
                            ? 'red'
                            : history.status === 'IN_PROGRESS' || history.status === 'in_progress'
                              ? 'blue'
                              : 'gray'
                      }
                    >
                      <div className="video-assets-history-item">
                        {/* Header with ID and Status */}
                        <div className="video-assets-history-item-header">
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            <ClockCircleOutlined style={{ marginRight: '4px' }} />
                            ID: {history.id}
                          </Text>
                          <Tag
                            color={getStatusDisplay(history.status).color}
                            className="video-assets-status-tag"
                          >
                            {getStatusDisplay(history.status).label}
                          </Tag>
                        </div>

                        {/* Action Title */}
                        <div className="video-assets-history-item-title">
                          <Text strong style={{ fontSize: '14px' }}>
                            {history.action}
                          </Text>
                        </div>

                        {/* User and Content Info */}
                        <div className="video-assets-history-item-user">
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            <UserOutlined style={{ marginRight: '4px' }} />
                            User: {history.user}
                          </Text>
                        </div>

                        <div className="video-assets-history-item-content">
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            Content ID: {history.content}
                          </Text>
                        </div>

                        {/* Additional Information */}
                        {history.extra_info && Object.keys(history.extra_info).length > 0 && (
                          <div className="video-assets-history-extra-info">
                            <Text type="secondary" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                              Additional Information:
                            </Text>
                            <div className="video-assets-history-extra-info-content">
                              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                {JSON.stringify(history.extra_info, null, 2)}
                              </pre>
                            </div>
                          </div>
                        )}
                      </div>
                    </Timeline.Item>
                  ))}
              </Timeline>
            ) : (
              <div className="video-assets-history-empty">
                <HistoryOutlined className="video-assets-history-empty-icon" />
                <Title level={4} type="secondary">
                  No action history available
                </Title>
                <Text type="secondary">
                  No actions have been performed yet for this content asset
                </Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Video selection modal */}
      <Modal
        title="📹 Select Video"
        open={videoSelectModalVisible}
        onCancel={closeVideoSelectModal}
        width={1200}
        footer={null}
        destroyOnClose
      >
        {/* Search filters */}
        <div className="video-assets-modal-search">
          <Row gutter={16}>
            <Col span={6}>
              <div className="video-assets-modal-search-item">
                <span>source</span>
                <Select
                  placeholder="source"
                  value={videoSourceFilter}
                  onChange={setVideoSourceFilter}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value="mediahold">mediahold</Option>
                  <Option value="upload">upload</Option>
                  <Option value="external">external</Option>
                </Select>
              </div>
            </Col>
            <Col span={6}>
              <div className="video-assets-modal-search-item">
                <span>status</span>
                <Select
                  placeholder="status"
                  value={videoStatusFilter}
                  onChange={setVideoStatusFilter}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value="processing">processing</Option>
                  <Option value="completed">completed</Option>
                  <Option value="failed">failed</Option>
                </Select>
              </div>
            </Col>
            <Col span={8}>
              <Input
                placeholder="search video"
                value={videoSearchQuery}
                onChange={e => setVideoSearchQuery(e.target.value)}
                onPressEnter={handleVideoSearch}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={4}>
              <Button type="primary" onClick={handleVideoSearch} style={{ width: '100%' }}>
                Search
              </Button>
            </Col>
          </Row>
        </div>

        {/* Video table */}
        <Table
          dataSource={videoSearchResult.data}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: videoSearchResult.count,
            showSizeChanger: true,
            showQuickJumper: false,
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          scroll={{ y: 400 }}
          columns={[
            {
              title: 'Asset id',
              dataIndex: 'id',
              key: 'id',
              width: '10%',
            },
            {
              title: 'ingest_source',
              dataIndex: 'ingest_source',
              key: 'ingest_source',
              width: '20%',
              render: (source: string) => source || '-',
            },
            {
              title: 'file_name',
              dataIndex: 'file_name',
              key: 'file_name',
              ellipsis: true,
              render: (name: string, record: VideoType) => (
                <div>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{name}</div>
                  {record.file_type && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {record.file_type} •{' '}
                      {record.file_size ? Math.round(record.file_size / (1024 * 1024)) : 0}MB
                    </div>
                  )}
                  {record.duration && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      Duration: {formatDuration(record.duration)}
                    </div>
                  )}
                </div>
              ),
            },
            {
              title: 'status',
              dataIndex: 'status',
              key: 'status',
              width: 100,
              render: (status: string) => (
                <Tag
                  color={
                    status === 'completed'
                      ? 'green'
                      : status === 'processing'
                        ? 'orange'
                        : status === 'failed'
                          ? 'red'
                          : 'default'
                  }
                >
                  {status || 'unknown'}
                </Tag>
              ),
            },
            {
              title: 'select',
              key: 'action',
              width: 100,
              render: (_, record: VideoType) => (
                <Button
                  type="default"
                  onClick={() => handleVideoSelect(record)}
                  style={{ width: '100%' }}
                >
                  Select
                </Button>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default VideoAssets;
